import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../shared/Layout';
import useCanvasViewport from '../../conversations-creator-canvas/useCanvasViewport';
import useNodeDrag from '../../conversations-creator-canvas/useNodeDrag';
import CanvasToolbar from './CanvasToolbar';
import CanvasNodeRenderer from './CanvasNodeRenderer';
import { buildCanvasNodes } from './canvasNodes';
import { getCanvasElements, createCanvasElement, updateCanvasElement, deleteCanvasElement, uploadCanvasImage, canvasFileUrl } from './canvasElementsApi';
import * as coursesApi from '../../courses/api';
import wsManager from '../../../services/wsManager';
import language from '../../../variables/language';
import VideoEmbedPreview from '../../../components/VideoEmbedPreview';
import '../../../css/topic-canvas.css';
import '../../../css/exercise-templates.css';

const TOPIC_ELEMENT_BASE = '/api/topicelements';
const TOPIC_CONTAINER_BASE = '/api/topiccontainers';
const CURSOR_SEND_THROTTLE_MS = 90;
const CURSOR_STALE_MS = 3000;
// Presence reuses the same /app/canvas-cursor channel as the live cursor
// (no new backend endpoint) - a periodic heartbeat keeps it accurate even
// while the other person's mouse sits still (a pure mousemove-driven
// signal would otherwise time out and flicker "gone" the moment someone
// stops moving, even though their tab is still open and watching).
// PRESENCE_STALE_MS is deliberately longer than CURSOR_STALE_MS (the dot's
// own timeout) so "is someone here" survives a couple of missed
// heartbeats even if the visible cursor dot itself briefly fades.
const PRESENCE_HEARTBEAT_MS = 2500;
const PRESENCE_STALE_MS = 7000;
const MY_ROLE = 'TEACHER';
// Distinct per-sender colors for a group-shared canvas's several
// simultaneous remote cursors - kept separate from the shape-drawing
// palette (CanvasToolbar's own colors), which has a near-white swatch that
// would be illegible as a cursor label.
const REMOTE_CURSOR_COLORS = ['#e0533a', '#4dd68c', '#7ec2e8', '#e0a35c', '#c792ea', '#f28fb1'];

function viewportStorageKey(workspaceId, topicId) {
   return 'canvas-viewport:' + workspaceId + ':' + topicId;
}
function readStoredViewport(workspaceId, topicId) {
   try {
      const raw = localStorage.getItem(viewportStorageKey(workspaceId, topicId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.zoom === 'number' && parsed.pan) return parsed;
   } catch (e) { /* ignore malformed/blocked storage, fall back to defaults */ }
   return null;
}

export default function TopicCanvasView() {
   const { studentId, courseId, topicId } = useParams();
   const lang = language[language.lang];

   const [status, setStatus] = useState('loading');
   const [workspace, setWorkspace] = useState(null);
   const [topic, setTopic] = useState(null);
   const [topicContent, setTopicContent] = useState({ elements: [], containers: [], exerciseLinks: [] });
   const [canvasElements, setCanvasElements] = useState([]);
   const [tool, setTool] = useState('select');
   const [color, setColor] = useState('#e0533a');
   const [draft, setDraft] = useState(null);
   const [textEditing, setTextEditing] = useState(null);
   const [videoEmbedDraft, setVideoEmbedDraft] = useState(null); // {url, description} while composing, else null
   // Keyed by the sender's own sourceWorkspaceId (backend-resolved, so it's
   // stable even though the platform side has no reliable Student.id of its
   // own) - a group-shared canvas can have several students' cursors live
   // at once now, not just one. Falls back to `role` as the key for a
   // message that predates the sourceWorkspaceId field (or a plain
   // one-on-one, non-group workspace, where role alone is already unique).
   const [cursors, setCursors] = useState({});
   // Same per-sender keying as `cursors` (sourceWorkspaceId, falling back to
   // role), but on its own longer timeout - decoupled from the visible
   // cursor dot's short one so "is this person still here" survives a
   // couple of missed heartbeats even while their mouse sits still and the
   // dot itself has faded. A group canvas can have several people present
   // at once now, so this is a map of everyone currently here, not a single
   // boolean - the old single "Студент присутній/відсутній" framing was
   // actively misleading once more than one student could be on the same
   // shared canvas (it only ever reflected whichever single cursor message
   // arrived most recently).
   const [presentByWorkspace, setPresentByWorkspace] = useState({});
   const [selectedNodeId, setSelectedNodeId] = useState(null);
   const [liveScale, setLiveScale] = useState(null); // { nodeId, scale } while a resize drag is in progress
   const cursorStaleTimers = useRef(new Map());
   const presenceStaleTimers = useRef(new Map());
   const lastCursorSentAt = useRef(0);
   const lastWorldPosRef = useRef({ x: 0, y: 0 });
   const resizeRef = useRef(null); // { nodeId, anchorX, anchorY, startDist, startScale } while resizing

   const storedViewport = useMemo(() => readStoredViewport(workspace && workspace.id, topicId), [workspace, topicId]);
   const viewport = useCanvasViewport({
      initialZoom: storedViewport ? storedViewport.zoom : 1,
      initialPan: storedViewport ? storedViewport.pan : { x: 0, y: 0 },
   });

   const loadCanvasElements = useCallback((workspaceId) => {
      if (!workspaceId) return;
      getCanvasElements(workspaceId, topicId)
         .then((res) => setCanvasElements(Array.isArray(res.data) ? res.data : []))
         .catch(() => setCanvasElements([]));
   }, [topicId]);

   useEffect(() => {
      let cancelled = false;
      setStatus('loading');
      // Eager get-or-create (not a plain GET+find) - a student's workspace
      // for a course is otherwise created lazily on their own first
      // platform activity. Opening the canvas from CRM before that ever
      // happens used to dead-end here (no workspace found -> error screen,
      // WS subscription below never starts, so the teacher's tab would
      // silently miss the student's very first draw/move forever, only
      // fixable by reloading). Confirmed live as a real bug, not assumed.
      axios.post('/api/studentcourseworkspaces', JSON.stringify({ student: studentId, course: courseId }), {
         withCredentials: true, headers: { 'Content-Type': 'application/json' },
      })
         .then((res) => {
            const ws = res.data;
            if (!ws || !ws.id) { if (!cancelled) setStatus('error'); return null; }
            return ws;
         })
         .then((ws) => {
            if (!ws || cancelled) return;
            setWorkspace(ws);
            return Promise.all([
               coursesApi.getOne('/api/topics', topicId),
               coursesApi.getList(TOPIC_ELEMENT_BASE, 'topic', topicId),
               coursesApi.getList(TOPIC_CONTAINER_BASE, 'topic', topicId),
               coursesApi.getTopicExerciseTemplates(topicId),
               // Tolerate /api/canvaselements not existing yet (backend
               // rollout is separate from this frontend change, same
               // resilience fix as the block-marker feature needed) - pins
               // still render with default positions even if this 404s.
               getCanvasElements(ws.id, topicId).catch(() => ({ data: [] })),
            ]).then(([topicRes, elementsRes, containersRes, linksRes, canvasRes]) => {
               if (cancelled) return;
               setTopic(topicRes.data);
               setTopicContent({
                  elements: Array.isArray(elementsRes.data) ? elementsRes.data : [],
                  containers: Array.isArray(containersRes.data) ? containersRes.data : [],
                  exerciseLinks: Array.isArray(linksRes.data) ? linksRes.data : [],
               });
               setCanvasElements(Array.isArray(canvasRes.data) ? canvasRes.data : []);
               setStatus('ready');
            });
         })
         .catch(() => { if (!cancelled) setStatus('error'); });
      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [studentId, courseId, topicId]);

   // Persist camera position locally only (never sent to the backend) -
   // per-browser, keyed by workspace+topic so different topics/students
   // remember independently.
   useEffect(() => {
      if (!workspace) return;
      try {
         localStorage.setItem(viewportStorageKey(workspace.id, topicId), JSON.stringify({ zoom: viewport.zoom, pan: viewport.pan }));
      } catch (e) { /* private-mode/blocked storage - camera position just won't persist */ }
   }, [workspace, topicId, viewport.zoom, viewport.pan]);

   const nodes = useMemo(() => buildCanvasNodes({ ...topicContent, canvasElements }), [topicContent, canvasElements]);

   const persistPayload = (node) => {
      const base = { workspace: workspace.id, topic: topicId, type: node.type, x: node.x, y: node.y };
      if (node.renderKind === 'PIN') {
         base[node.pinKind] = node.targetId;
         return base;
      }
      return { ...base, width: node.width, height: node.height, color: node.color, config: JSON.stringify(node.config || {}) };
   };
   const handleDragEnd = (updatedNode) => {
      let node = updatedNode;
      // PENCIL is the one type whose visual shape lives in config (an
      // absolute points array) rather than being fully described by
      // x/y/width/height - a plain translate-drag only ever moves x/y
      // (see useNodeDrag.js), so without this the points array goes stale
      // the moment a stroke is dragged: this app's own renderer tolerates
      // it (it re-derives the path's offset from the points themselves,
      // ignoring x/y), but any other reader that trusts x/y as the
      // stroke's real anchor - which is the only sane way to read it -
      // would keep showing the pre-drag position forever. Confirmed via a
      // real drifted row (x/y ~800px off from min(points)) that produced
      // exactly this symptom cross-app. Shifting points by the same delta
      // as x/y keeps the data itself absolute-correct for every consumer.
      if (node.type === 'PENCIL' && node.config && Array.isArray(node.config.points)) {
         const orig = nodes.find((n) => n.id === node.id);
         if (orig && (orig.x !== node.x || orig.y !== node.y)) {
            const dx = node.x - orig.x, dy = node.y - orig.y;
            node = { ...node, config: { ...node.config, points: node.config.points.map((p) => [p[0] + dx, p[1] + dy]) } };
         }
      }
      const payload = persistPayload(node);
      const request = node.persisted ? updateCanvasElement(node.elementId, payload) : createCanvasElement(payload);
      request.then(() => loadCanvasElements(workspace.id)).catch(() => {});
   };
   // For a PIN, deleting just removes its saved position (the underlying
   // topic content is untouched) - it reappears at its default spot next
   // load, same as any never-dragged pin. For a freeform annotation it's a
   // real delete. Nothing to delete for a pin that was never dragged (no
   // elementId yet) - the toolbar's trash button is disabled in that case
   // (hasSelection only reflects a *persisted* node, see below).
   const handleDeleteSelected = () => {
      const node = dragNodes.find((n) => n.id === selectedNodeId);
      if (!node || !node.persisted || !node.elementId) return;
      deleteCanvasElement(node.elementId).then(() => {
         setSelectedNodeId(null);
         loadCanvasElements(workspace.id);
      }).catch(() => {});
   };

   const { nodes: dragNodes, draggingNodeId, startNodeDrag, handleDragMove, handleDragEnd: finishDrag } = useNodeDrag({
      initialNodes: nodes,
      screenToWorld: viewport.screenToWorld,
      onDragEnd: handleDragEnd,
   });

   const commitDraft = () => {
      if (!draft) return;
      let payload = null;
      if (draft.type === 'PENCIL') {
         if (draft.points.length >= 2) {
            const xs = draft.points.map((p) => p[0]), ys = draft.points.map((p) => p[1]);
            payload = {
               workspace: workspace.id, topic: topicId, type: 'PENCIL',
               x: Math.min(...xs), y: Math.min(...ys), color,
               config: JSON.stringify({ points: draft.points }),
            };
         }
      } else if (draft.type === 'ARROW') {
         // config stores the end point as a RELATIVE offset (dx/dy) from
         // x/y, not an absolute x2/y2 - x/y is the only thing a
         // translate-drag ever updates (useNodeDrag.js), so the end point
         // must move together with it automatically instead of needing its
         // own drag handling. See CanvasNodeRenderer.jsx's ARROW branch for
         // the full explanation of the bug this fixes.
         payload = {
            workspace: workspace.id, topic: topicId, type: 'ARROW',
            x: draft.x, y: draft.y, width: Math.abs(draft.x2 - draft.x), height: Math.abs(draft.y2 - draft.y),
            color, config: JSON.stringify({ dx: draft.x2 - draft.x, dy: draft.y2 - draft.y }),
         };
      } else {
         const x = Math.min(draft.x, draft.x2), y = Math.min(draft.y, draft.y2);
         const width = Math.max(Math.abs(draft.x2 - draft.x), 10), height = Math.max(Math.abs(draft.y2 - draft.y), 10);
         payload = { workspace: workspace.id, topic: topicId, type: draft.type, x, y, width, height, color, config: '{}' };
      }
      setDraft(null);
      // Narzędzie zostaje aktywne po dodaniu elementu (ołówek/prostokąt/
      // elipsa/strzałka) - żeby dało się dorysować kolejny bez ponownego
      // klikania w toolbar za każdym razem (to samo zgłoszenie co w
      // TopicCanvasView.tsx po stronie studenckiej appki).
      if (payload) createCanvasElement(payload).then(() => loadCanvasElements(workspace.id)).catch(() => {});
   };

   const sendCursor = (world) => {
      lastWorldPosRef.current = world;
      const now = Date.now();
      if (now - lastCursorSentAt.current < CURSOR_SEND_THROTTLE_MS) return;
      lastCursorSentAt.current = now;
      if (!workspace) return;
      wsManager.send('/app/canvas-cursor', { workspaceId: workspace.id, topicId: Number(topicId), role: MY_ROLE, x: world.x, y: world.y });
   };

   // Resize is a uniform CSS-scale drag (not a font-size control, not a
   // width/height reflow) anchored at the node's own top-left corner
   // (node.x/node.y never move) - drag distance from that anchor, relative
   // to the distance at drag-start, gives the new scale factor. Works
   // identically for every non-PIN type (pencil paths, shapes, text, image)
   // without any per-type resize math. Pins are never resizable - they
   // represent the topic's own content, not something to scale.
   const startResize = (e, node) => {
      e.stopPropagation();
      e.preventDefault();
      const world = viewport.screenToWorld(e.clientX, e.clientY);
      const dist = Math.max(Math.hypot(world.x - node.x, world.y - node.y), 1);
      const startScale = (node.config && node.config.scale) || 1;
      resizeRef.current = { nodeId: node.id, anchorX: node.x, anchorY: node.y, startDist: dist, startScale };
      setSelectedNodeId(node.id);
      setLiveScale({ nodeId: node.id, scale: startScale });
   };
   const finishResize = () => {
      const r = resizeRef.current;
      resizeRef.current = null;
      if (!r) return;
      const node = dragNodes.find((n) => n.id === r.nodeId);
      setLiveScale(null);
      if (!node) return;
      const finalScale = liveScale && liveScale.nodeId === r.nodeId ? liveScale.scale : r.startScale;
      const payload = persistPayload({ ...node, config: { ...(node.config || {}), scale: finalScale } });
      const request = node.persisted ? updateCanvasElement(node.elementId, payload) : createCanvasElement(payload);
      request.then(() => loadCanvasElements(workspace.id)).catch(() => {});
   };

   const handleMouseDown = (e) => {
      if (e.button === 1) { viewport.startPan(e); return; }
      if (e.button !== 0) return;
      if (tool === 'select') { setSelectedNodeId(null); return; }
      e.preventDefault();
      const world = viewport.screenToWorld(e.clientX, e.clientY);
      if (tool === 'PENCIL') setDraft({ type: 'PENCIL', points: [[world.x, world.y]] });
      // Narzędzie NIE wraca do 'select' tutaj - to samo zgłoszenie co po
      // stronie studenckiej appki (TopicCanvasView.tsx): przełączanie się od
      // razu w mousedown, zanim użytkownik zdążył cokolwiek wpisać, czytało
      // się jako "puszczam myszkę i natychmiast przełącza na wybór
      // elementu". commitText() niżej też świadomie nie rusza tool.
      else if (tool === 'TEXT') setTextEditing({ x: world.x, y: world.y, value: '' });
      else setDraft({ type: tool, x: world.x, y: world.y, x2: world.x, y2: world.y });
   };
   const handleMouseMove = (e) => {
      const world = viewport.screenToWorld(e.clientX, e.clientY);
      sendCursor(world);
      if (resizeRef.current) {
         const r = resizeRef.current;
         const dist = Math.hypot(world.x - r.anchorX, world.y - r.anchorY);
         const nextScale = Math.min(Math.max(r.startScale * (dist / r.startDist), 0.2), 6);
         setLiveScale({ nodeId: r.nodeId, scale: nextScale });
         return;
      }
      if (viewport.isPanning) { viewport.handlePanMove(e); return; }
      if (draft) {
         if (draft.type === 'PENCIL') setDraft((prev) => ({ ...prev, points: [...prev.points, [world.x, world.y]] }));
         else setDraft((prev) => ({ ...prev, x2: world.x, y2: world.y }));
         return;
      }
      handleDragMove(e);
   };
   const handleMouseUp = () => {
      if (resizeRef.current) { finishResize(); return; }
      viewport.endPan();
      finishDrag();
      if (draft) commitDraft();
   };

   const handlePickImage = (file) => {
      uploadCanvasImage(file, workspace.id).then((res) => {
         const url = canvasFileUrl(res.data);
         const rect = viewport.viewportRef.current.getBoundingClientRect();
         const center = viewport.screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2);
         return createCanvasElement({
            workspace: workspace.id, topic: topicId, type: 'IMAGE',
            x: center.x - 120, y: center.y - 80, width: 240, height: null,
            config: JSON.stringify({ fileUrl: url }),
         });
      }).then(() => { loadCanvasElements(workspace.id); setTool('select'); }).catch(() => setTool('select'));
   };

   // Opens the compose panel immediately on toolbar click (same "acts right
   // away, no draw gesture needed" UX as IMAGE's file picker) rather than
   // requiring a canvas click first like TEXT/shapes do - there's nothing to
   // draw, just a link to paste.
   const handleAddVideoEmbed = () => {
      setTool('VIDEO_EMBED');
      setVideoEmbedDraft({ url: '', description: '' });
   };
   const cancelVideoEmbed = () => {
      setVideoEmbedDraft(null);
      setTool('select');
   };
   const commitVideoEmbed = () => {
      if (!videoEmbedDraft || !videoEmbedDraft.url.trim()) { cancelVideoEmbed(); return; }
      const rect = viewport.viewportRef.current.getBoundingClientRect();
      const center = viewport.screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2);
      createCanvasElement({
         workspace: workspace.id, topic: topicId, type: 'VIDEO_EMBED',
         x: center.x - 240, y: center.y - 135, width: 480, height: 270,
         config: JSON.stringify({ url: videoEmbedDraft.url.trim(), description: videoEmbedDraft.description }),
      }).then(() => { loadCanvasElements(workspace.id); cancelVideoEmbed(); }).catch(() => cancelVideoEmbed());
   };

   const commitText = () => {
      if (!textEditing || !textEditing.value.trim()) { setTextEditing(null); return; }
      createCanvasElement({
         workspace: workspace.id, topic: topicId, type: 'TEXT',
         x: textEditing.x, y: textEditing.y, width: 200, color,
         config: JSON.stringify({ text: textEditing.value }),
      }).then(() => { loadCanvasElements(workspace.id); setTextEditing(null); }).catch(() => setTextEditing(null));
   };

   // WS: element sync (any add/update/delete -> simple refetch, same idiom
   // as notes/markers elsewhere) + ephemeral cursor relay (never persisted,
   // auto-clears after CURSOR_STALE_MS of silence from the other side).
   useEffect(() => {
      if (status !== 'ready' || !workspace) return undefined;
      const elementsTopic = '/topic/workspace/' + workspace.id;
      const cursorTopic = '/topic/workspace/' + workspace.id + '/canvas/' + topicId + '/cursor';
      const staleTimers = cursorStaleTimers.current;
      const presenceTimers = presenceStaleTimers.current;

      const elementsHandler = (event) => {
         if (event.type === 'canvas_element_added' || event.type === 'canvas_element_updated' || event.type === 'canvas_element_deleted') {
            loadCanvasElements(workspace.id);
         }
      };
      const cursorHandler = (msg) => {
         if (!msg || msg.role === MY_ROLE) return;
         const key = msg.sourceWorkspaceId != null ? String(msg.sourceWorkspaceId) : msg.role;
         setCursors(prev => ({ ...prev, [key]: { x: msg.x, y: msg.y, role: msg.role, name: msg.name } }));
         if (staleTimers.has(key)) clearTimeout(staleTimers.get(key));
         staleTimers.set(key, setTimeout(() => {
            setCursors(prev => { const next = { ...prev }; delete next[key]; return next; });
            staleTimers.delete(key);
         }, CURSOR_STALE_MS));
         // Presence tracked separately from the visible cursor dot, per
         // sender - any message from someone else (a real mouse move OR a
         // heartbeat sent below even while their mouse sits still) counts
         // as "still here", with its own longer timeout so it survives a
         // couple of missed heartbeats even if the dot itself briefly fades.
         setPresentByWorkspace(prev => ({ ...prev, [key]: { role: msg.role, name: msg.name } }));
         if (presenceTimers.has(key)) clearTimeout(presenceTimers.get(key));
         presenceTimers.set(key, setTimeout(() => {
            setPresentByWorkspace(prev => { const next = { ...prev }; delete next[key]; return next; });
            presenceTimers.delete(key);
         }, PRESENCE_STALE_MS));
      };

      wsManager.subscribe(elementsTopic, elementsHandler);
      wsManager.subscribe(cursorTopic, cursorHandler);

      // Heartbeat: announce "I'm here" on this canvas even if the mouse
      // never moves - fires once immediately (so presence shows up fast on
      // open, not after waiting a full interval) then on a fixed interval.
      const sendHeartbeat = () => {
         wsManager.send('/app/canvas-cursor', {
            workspaceId: workspace.id, topicId: Number(topicId), role: MY_ROLE,
            x: lastWorldPosRef.current.x, y: lastWorldPosRef.current.y,
         });
      };
      sendHeartbeat();
      const heartbeatInterval = setInterval(sendHeartbeat, PRESENCE_HEARTBEAT_MS);

      return () => {
         wsManager.unsubscribe(elementsTopic, elementsHandler);
         wsManager.unsubscribe(cursorTopic, cursorHandler);
         clearInterval(heartbeatInterval);
         staleTimers.forEach(timer => clearTimeout(timer));
         staleTimers.clear();
         presenceTimers.forEach(timer => clearTimeout(timer));
         presenceTimers.clear();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [status, workspace, topicId]);

   const backTo = '/students/' + studentId + '/workspace/' + courseId + '?topic=' + topicId;

   // Not wrapped in .main-block/PageHeader like a normal page - this route
   // has no header chrome even when ready (the canvas viewport below fills
   // the full 100vh itself), and .main-block's flex:1 relies on a
   // PageHeader sibling to get any height at all - without one these
   // states rendered fully blank (confirmed live via screenshot, not
   // assumed). A plain centered block needs no such context.
   if (status === 'loading' || status === 'error') {
      return (
         <Layout>
            <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--maintext-color)' }}>
               {status === 'loading' ? lang['Loading ...'] : lang.error}
            </div>
         </Layout>
      );
   }

   return (
      <Layout>
         <div ref={viewport.viewportRef}
            className='topic-canvas-viewport'
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: tool !== 'select' ? 'crosshair' : (viewport.isPanning ? 'grabbing' : 'default') }}>
            <div className='topic-canvas-world' style={{ transform: `translate(${viewport.pan.x}px, ${viewport.pan.y}px) scale(${viewport.zoom})` }}>
               {dragNodes.map((node) => {
                  const scale = (liveScale && liveScale.nodeId === node.id) ? liveScale.scale : ((node.config && node.config.scale) || 1);
                  const isSelected = selectedNodeId === node.id;
                  return (
                     <div key={node.id}
                        className='topic-canvas-node'
                        data-node-id={node.id}
                        data-element-id={node.elementId || ''}
                        onMouseDown={(e) => {
                           // Only a left-click selects/drags - a middle-click here must
                           // fall through to the viewport's own pan handler untouched
                           // (startNodeDrag already no-ops for e.button !== 0 internally
                           // and skips stopPropagation, but selecting on a middle-click
                           // was still happening unconditionally here - caught via a
                           // live pan-over-an-element test after the platform session
                           // found the same class of bug in their own implementation).
                           if (tool === 'select' && e.button === 0) { setSelectedNodeId(node.id); startNodeDrag(e, node); }
                        }}
                        style={{ left: node.x, top: node.y, cursor: tool === 'select' ? (draggingNodeId === node.id ? 'grabbing' : 'grab') : 'default' }}>
                        <div className={'topic-canvas-node-scale' + (isSelected ? ' selected' : '')}
                           style={{
                              transform: scale !== 1 ? `scale(${scale})` : undefined,
                              // Explicit size (when known) instead of relying on
                              // auto-sizing to a normal-flow child - RECT/ELLIPSE's
                              // own child already matches this so it's a no-op
                              // there, but ARROW's content is position:absolute
                              // (needs to reach into negative offsets when it
                              // points up/left) and would otherwise collapse this
                              // wrapper to zero size, throwing off the resize
                              // handle's own bottom-right anchor.
                              width: node.width || undefined, height: node.height || undefined,
                           }}>
                           <CanvasNodeRenderer node={node} allElements={topicContent.elements} />
                           {isSelected && node.renderKind !== 'PIN' ? (
                              <span className='topic-canvas-resize-handle' onMouseDown={(e) => startResize(e, node)} />
                           ) : ''}
                        </div>
                     </div>
                  );
               })}
               {draft && draft.type !== 'PENCIL' ? (
                  <div className='topic-canvas-draft-shape' style={{
                     left: Math.min(draft.x, draft.x2), top: Math.min(draft.y, draft.y2),
                     width: Math.abs(draft.x2 - draft.x), height: Math.abs(draft.y2 - draft.y),
                     borderColor: color,
                  }} />
               ) : ''}
               {draft && draft.type === 'PENCIL' && draft.points.length >= 2 ? (
                  <div className='topic-canvas-node' style={{
                     left: Math.min(...draft.points.map((p) => p[0])),
                     top: Math.min(...draft.points.map((p) => p[1])),
                  }}>
                     <CanvasNodeRenderer node={{ renderKind: 'PENCIL', config: { points: draft.points }, color }} />
                  </div>
               ) : ''}
               {textEditing ? (
                  <textarea autoFocus className='topic-canvas-text-input' style={{ left: textEditing.x, top: textEditing.y, color }}
                     value={textEditing.value}
                     onChange={(e) => setTextEditing((prev) => ({ ...prev, value: e.target.value }))}
                     onBlur={commitText}
                     onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitText(); } }} />
               ) : ''}
               {Object.keys(cursors).map((key, i) => {
                  const cursor = cursors[key];
                  const label = cursor.name || (cursor.role === 'STUDENT' ? lang.canvasStudentCursor : lang.canvasTeacherCursor);
                  return (
                     <div key={key} className='topic-canvas-remote-cursor' data-cursor-color={i % REMOTE_CURSOR_COLORS.length}
                        style={{ left: cursor.x, top: cursor.y, '--remote-cursor-color': REMOTE_CURSOR_COLORS[i % REMOTE_CURSOR_COLORS.length] }}>
                        <span className='topic-canvas-remote-cursor-dot' />
                        <span className='topic-canvas-remote-cursor-label'>{label}</span>
                     </div>
                  );
               })}
            </div>
            {videoEmbedDraft ? (
               <div className='topic-canvas-video-embed-panel' onMouseDown={(e) => e.stopPropagation()}>
                  <div className='exercise-block-field'>
                     <label className='exercise-block-field-label'>{lang.videoEmbedUrlLabel}</label>
                     <input autoFocus className='exercise-block-generic-text' value={videoEmbedDraft.url}
                        placeholder={lang.videoEmbedUrlPlaceholder}
                        onChange={(e) => setVideoEmbedDraft((prev) => ({ ...prev, url: e.target.value }))} />
                  </div>
                  <div className='exercise-block-field'>
                     <label className='exercise-block-field-label'>{lang.videoEmbedDescriptionLabel}</label>
                     <input className='exercise-block-generic-text' value={videoEmbedDraft.description}
                        placeholder={lang.videoEmbedDescriptionPlaceholder}
                        onChange={(e) => setVideoEmbedDraft((prev) => ({ ...prev, description: e.target.value }))} />
                  </div>
                  <VideoEmbedPreview url={videoEmbedDraft.url} description='' />
                  <div className='exercise-block-editor-actions'>
                     <button type='button' className='exercise-block-save-btn' onClick={commitVideoEmbed}>{lang.videoEmbedSave}</button>
                     <button type='button' className='exercise-block-cancel-btn' onClick={cancelVideoEmbed}>{lang.videoEmbedCancel}</button>
                  </div>
               </div>
            ) : ''}
            <CanvasToolbar tool={tool} setTool={setTool} color={color} setColor={setColor}
               onPickImage={handlePickImage} onAddVideoEmbed={handleAddVideoEmbed} viewport={viewport} backTo={backTo} lang={lang}
               hasSelection={!!dragNodes.find((n) => n.id === selectedNodeId && n.persisted && n.elementId)}
               onDeleteSelected={handleDeleteSelected}
               presentList={Object.values(presentByWorkspace).map(p => p.name || lang.canvasStudentCursor)} />
            {topic ? <span className='topic-canvas-title-label'>{topic.name}</span> : ''}
         </div>
      </Layout>
   );
}
